import { CopyAlt, DotsEditCustom, Edit, Upload } from '@profgeosoft-ui/icons';
import { Tooltip as GOTooltip, Button as GOButton, Loader } from '@profgeosoft-ui/react';
import { Input, Tooltip } from 'antd';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Checkmark from 'src/shared/assets/icons/checkmark.svg';
import Cross from 'src/shared/assets/icons/cross.svg';
import DeleteIcon from 'src/shared/assets/icons/delete.svg';
import { Button } from 'src/shared/components/button';
import { useStore } from 'src/store';
import type { TPlanVersion } from 'src/store/comparison/types';

import styles from './versions-list-item.module.scss';

interface Props {
  version: TPlanVersion;
  isRenamingAvailable: boolean;
  isPersonalDraftCreationAvailable?: boolean;
  onCopyVersion?(parentVersion: TPlanVersion): void;
  onVersionChange?(planVersion: TPlanVersion): void;
  onRename?(id: number, newName: string): Promise<void>;
  onDeleteVersion?(planVersion: TPlanVersion): void;
  onMakeIntegrated?(planVersion: TPlanVersion): void;
}

export const VersionsListItem = observer(function VersionsListItem({
  version,
  isRenamingAvailable,
  isPersonalDraftCreationAvailable,
  onCopyVersion,
  onVersionChange,
  onRename,
  onDeleteVersion,
  onMakeIntegrated,
}: Props) {
  const { t } = useTranslation();
  const { planVersion } = useStore();
  const [isRenaming, setIsRenaming] = useState(false);
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const [versionName, setVersionName] = useState<string>(version.data.name);

  const shouldTooltipBeDisplayed =
    (!!onCopyVersion || !!onRename || !!onMakeIntegrated) && !planVersion.integrationInProgressVersions.has(version.id);

  const handleCancelRenaming = () => {
    setVersionName(version.data.name);
    setIsRenaming(false);
  };

  const handleApplyRenaming = () => {
    if (versionName !== version.data.name) {
      onRename?.(version.id, versionName);
    }

    setIsRenaming(false);
  };

  useEffect(() => {
    setVersionName((name) => {
      if (name !== version.data.name) {
        return version.data.name;
      }

      return name;
    });
  }, [version, version.data.name]);

  return (
    <li className={clsx(styles.listItem, !onDeleteVersion && styles.listItem_undeletable)}>
      {!!onDeleteVersion && (
        <button className={styles.listItemButton} onClick={() => onDeleteVersion(version)}>
          <DeleteIcon className={styles.listItemButtonIcon} />
        </button>
      )}

      {!isRenaming && (
        <div className={styles.nameBlock}>
          <p className={styles.listItemText} onClick={() => onVersionChange?.(version)}>
            {versionName}
          </p>
          {planVersion.integrationInProgressVersions.has(version.id) && (
            <Loader size="s" style={{ position: 'static', backgroundColor: 'unset', width: 24, height: 24 }} />
          )}
          {shouldTooltipBeDisplayed && (
            <GOTooltip
              open={isTooltipOpened}
              gap={-4}
              onOpenChange={(is) => setIsTooltipOpened(is)}
              title={
                <ul className={styles.contextTooltipContainer}>
                  {onCopyVersion && (
                    <Tooltip
                      trigger={!isPersonalDraftCreationAvailable ? ['hover'] : []}
                      defaultVisible={false}
                      title={t('modals:EditPublicDraftModal.unableToCreatePrivateDraft')}
                      placement="topRight"
                      align={{
                        offset: [12, 0],
                      }}
                    >
                      {/* div необходим, т.к. иначе antd отказывается показывать тултип */}
                      <li className={styles.contextTooltipItem}>
                        <Button
                          className={styles.versionItemButton}
                          disabled={!isPersonalDraftCreationAvailable}
                          prefixIcon={<CopyAlt className={styles.icon} />}
                          onClick={() => {
                            onCopyVersion(version);
                            setIsTooltipOpened(false);
                          }}
                        >
                          <p className="caption">Копировать версию</p>
                        </Button>
                      </li>
                    </Tooltip>
                  )}
                  {isRenamingAvailable && (
                    <li className={styles.contextTooltipItem}>
                      <Button
                        className={styles.versionItemButton}
                        prefixIcon={<Edit className={styles.icon} />}
                        onClick={() => {
                          setIsRenaming((isRenaming) => !isRenaming);
                          setIsTooltipOpened(false);
                        }}
                      >
                        <p className="caption">Редактировать название</p>
                      </Button>
                    </li>
                  )}
                  {/* {onMakeIntegrated && !version.data.isIntegrated && (
                    <li>
                      <Button
                        className={styles.versionItemButton}
                        prefixIcon={<Upload className={styles.icon} />}
                        onClick={() => {
                          onMakeIntegrated(version);
                          setIsTooltipOpened(false);
                        }}
                      >
                        <p className="caption">Интегрировать</p>
                      </Button>
                    </li>
                  )} */}
                  {onMakeIntegrated && (
                    <li>
                      <Button
                        className={styles.versionItemButton}
                        prefixIcon={<Upload className={styles.icon} />}
                        onClick={() => {
                          onMakeIntegrated(version);
                          setIsTooltipOpened(false);
                        }}
                      >
                        <p className="caption">Интегрировать</p>
                      </Button>
                    </li>
                  )}
                  {/* {onMakeIntegrated && version.data.isIntegrated && (
                    <li className={styles.versionItemIntegrated}>
                      <CheckRingLight />
                      <p className="caption">Версия интегрирована</p>
                    </li>
                  )} */}
                </ul>
              }
            >
              <GOButton
                variant="flat"
                style={{
                  width: 24,
                  height: 24,
                }}
                icon={<DotsEditCustom style={{ color: 'var(--go-text)' }} />}
              />
            </GOTooltip>
          )}
        </div>
      )}
      {isRenaming && (
        <div className={styles.renameBlock}>
          <Input.TextArea
            className={styles.textArea}
            value={versionName}
            autoSize={{ minRows: 1 }}
            rows={1}
            allowClear
            onChange={(e) => setVersionName(e.target.value)}
          />
          <Button
            width={24}
            height={24}
            className={styles.renameButton}
            icon={<Cross width={16} />}
            variant="danger"
            onClick={handleCancelRenaming}
          />
          <Button
            width={24}
            height={24}
            className={styles.renameButton}
            icon={<Checkmark width={16} />}
            variant="success"
            disabled={versionName.length < 1}
            onClick={handleApplyRenaming}
          />
        </div>
      )}
    </li>
  );
});
